import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Stack, Plus, Trash, FloppyDisk, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_RUNGS = [
  { from_rank: 1, to_rank: 2, baseline_elo: 2500, tier: "premier" },
  { from_rank: 3, to_rank: 4, baseline_elo: 2300, tier: "premier" },
  { from_rank: 5, to_rank: 6, baseline_elo: 2000, tier: "premier" },
  { from_rank: 7, to_rank: 8, baseline_elo: 1700, tier: "premier" },
  { from_rank: 9, to_rank: 10, baseline_elo: 1400, tier: "premier" },
];
const DEFAULT_FALLBACK = { baseline_elo: 1000, tier: "challenger" };

/**
 * Editable Tier Ladder. Single source of truth for the rank → baseline-ELO
 * mapping used by:
 *   - Admin → Settings → "Repair Corrupted Tournament ELO" card
 *   - Admin → Players → "ELO Order & Baselines" → "Apply tier ladder" button
 *
 * Constraints enforced server-side:
 *   - rungs are contiguous (from_rank = previous to_rank + 1)
 *   - baselines in [100, 4000]
 *   - tier in {"premier", "challenger"}
 */
export default function TierLadderCard() {
  const [rungs, setRungs] = useState(DEFAULT_RUNGS);
  const [fallback, setFallback] = useState(DEFAULT_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/tier-ladder`, { withCredentials: true });
      setRungs(r.data?.rungs || DEFAULT_RUNGS);
      setFallback(r.data?.fallback || DEFAULT_FALLBACK);
    } catch (err) {
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Failed to load tier ladder");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRung = (idx, patch) => {
    setRungs((cur) => cur.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRung = () => {
    setRungs((cur) => {
      const last = cur[cur.length - 1];
      const start = last ? last.to_rank + 1 : 1;
      return [
        ...cur,
        { from_rank: start, to_rank: start + 1, baseline_elo: Math.max(100, (last?.baseline_elo || 2500) - 300), tier: "premier" },
      ];
    });
  };

  const removeRung = (idx) => setRungs((cur) => cur.filter((_, i) => i !== idx));

  const resetDefaults = () => {
    setRungs(DEFAULT_RUNGS);
    setFallback(DEFAULT_FALLBACK);
    toast.success("Reset to default ladder (2500/2300/2000/1700/1400 + 1000)");
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/tier-ladder`, { rungs, fallback }, { withCredentials: true });
      toast.success("Tier ladder saved — Repair card and Order Editor will use these values");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-secondary/30" data-testid="tier-ladder-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Stack className="w-5 h-5 text-secondary" weight="fill" />
          Tier Ladder Settings
        </CardTitle>
        <CardDescription className="text-xs leading-relaxed">
          Define the baseline ELO per rank pair. Used by the Repair card and the
          ELO Order Editor's "Apply tier ladder" button. Rungs must be contiguous
          starting at rank 1.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-xs text-muted-foreground italic">Loading…</p>
        ) : (
          <>
            <div className="space-y-2">
              {rungs.map((r, idx) => (
                <div
                  key={idx}
                  className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-md bg-background/40 border border-border/50"
                  data-testid={`tier-ladder-rung-${idx}`}
                >
                  <span className="text-xs text-muted-foreground w-16">Rank</span>
                  <Input
                    type="number" min="1" value={r.from_rank}
                    onChange={(e) => updateRung(idx, { from_rank: parseInt(e.target.value) || 1 })}
                    className="h-8 w-16 text-xs tabular-nums"
                    data-testid={`tier-ladder-from-${idx}`}
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input
                    type="number" min="1" value={r.to_rank}
                    onChange={(e) => updateRung(idx, { to_rank: parseInt(e.target.value) || 1 })}
                    className="h-8 w-16 text-xs tabular-nums"
                    data-testid={`tier-ladder-to-${idx}`}
                  />
                  <span className="text-xs text-muted-foreground ml-2">→</span>
                  <Input
                    type="number" step="50" min="100" max="4000" value={r.baseline_elo}
                    onChange={(e) => updateRung(idx, { baseline_elo: parseInt(e.target.value) || 0 })}
                    className="h-8 w-24 text-xs tabular-nums"
                    placeholder="ELO"
                    data-testid={`tier-ladder-elo-${idx}`}
                  />
                  <select
                    value={r.tier}
                    onChange={(e) => updateRung(idx, { tier: e.target.value })}
                    className="h-8 text-xs bg-background border border-border rounded px-2"
                    data-testid={`tier-ladder-tier-${idx}`}
                  >
                    <option value="premier">Premier</option>
                    <option value="challenger">Challenger</option>
                  </select>
                  <Button
                    size="sm" variant="ghost" onClick={() => removeRung(idx)}
                    className="h-7 w-7 p-0 ml-auto text-destructive hover:bg-destructive/10"
                    data-testid={`tier-ladder-remove-${idx}`}
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-md bg-muted/10 border border-dashed border-border">
              <span className="text-xs text-muted-foreground w-32 italic">Everyone else →</span>
              <Input
                type="number" step="50" min="100" max="4000" value={fallback.baseline_elo}
                onChange={(e) => setFallback({ ...fallback, baseline_elo: parseInt(e.target.value) || 0 })}
                className="h-8 w-24 text-xs tabular-nums"
                data-testid="tier-ladder-fallback-elo"
              />
              <select
                value={fallback.tier}
                onChange={(e) => setFallback({ ...fallback, tier: e.target.value })}
                className="h-8 text-xs bg-background border border-border rounded px-2"
                data-testid="tier-ladder-fallback-tier"
              >
                <option value="challenger">Challenger</option>
                <option value="premier">Premier</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                size="sm" variant="outline" onClick={addRung}
                className="border-border text-xs"
                data-testid="tier-ladder-add-rung-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" weight="bold" />
                Add rung
              </Button>
              <Button
                size="sm" variant="outline" onClick={resetDefaults}
                className="border-border text-xs"
                data-testid="tier-ladder-reset-btn"
              >
                <ArrowsCounterClockwise className="w-3.5 h-3.5 mr-1" weight="bold" />
                Reset to defaults
              </Button>
              <Button
                size="sm" onClick={save} disabled={saving}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-xs ml-auto"
                data-testid="tier-ladder-save-btn"
              >
                <FloppyDisk className="w-3.5 h-3.5 mr-1" weight="fill" />
                {saving ? "Saving…" : "Save ladder"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
